import { Check, Plus, X } from "lucide-react";

import CONSTANTS from "constants/Constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import ValidatePhoneNumberModal from "components/modals/ValidatePhoneNumberModal";

const contactPhoneTypes = CONSTANTS.CONTACT_PHONE_TYPES;

const PHONE_TYPE_OPTIONS = Object.keys(contactPhoneTypes).map(key => ({
  key: parseInt(contactPhoneTypes[key]),
  value: parseInt(contactPhoneTypes[key]),
  text: key,
}));

const typeTextByValue = new Map(PHONE_TYPE_OPTIONS.map(o => [o.value, o.text]));

function makeNewNumber(type) {
  return {
    is_primary: false,
    number: "",
    ext: "",
    type: contactPhoneTypes.DEFAULT,
    enabled: type !== "user",
    is_callable: true,
  };
}

const HEADER_CLASS =
  "tw:text-left tw:text-xs tw:font-medium tw:text-(--color-text-secondary) tw:pb-2 tw:px-2";
const CELL_CLASS = "tw:px-2 tw:py-2 tw:align-middle";

const PhoneNumbersInput = ({ value = [], onChange, type }) => {
  const isUser = type === "user";

  const editField = (index, field, fieldValue) => {
    const next = value.map((pn, i) =>
      i === index ? { ...pn, [field]: fieldValue } : pn
    );
    // A contact can only have one primary number, so selecting one clears it
    // on every other row.
    if (field === "is_primary" && fieldValue) {
      next.forEach((pn, i) => {
        if (i !== index) pn.is_primary = false;
      });
    }
    onChange(next);
  };

  const addNumber = () => onChange([...value, makeNewNumber(type)]);

  const removeNumber = index => onChange(value.filter((_, i) => i !== index));

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      {value.length > 0 ? (
        <table className="tw:w-full tw:text-sm">
          <thead>
            <tr className="tw:border-b tw:border-(--color-border)">
              <th className={HEADER_CLASS}>Number</th>
              <th className={HEADER_CLASS}>Ext</th>
              <th className={HEADER_CLASS}>Type</th>
              <th className={HEADER_CLASS}>Primary?</th>
              {isUser ? (
                <th className={HEADER_CLASS}>Validated</th>
              ) : (
                <>
                  <th className={HEADER_CLASS}>Enabled?</th>
                  <th className={HEADER_CLASS}>Callable?</th>
                </>
              )}
              <th className={HEADER_CLASS} />
            </tr>
          </thead>
          <tbody>
            {value.map((phoneNumber, index) => (
              <tr
                key={index}
                className="tw:border-b tw:border-(--color-border)"
              >
                <td className={CELL_CLASS}>
                  <PhoneInput
                    placeholder="Enter Phone Number"
                    value={phoneNumber.number}
                    onChange={number => editField(index, "number", number)}
                  />
                </td>
                <td className={CELL_CLASS}>
                  <Input
                    placeholder="Ext."
                    value={phoneNumber.ext ?? ""}
                    onChange={e => editField(index, "ext", e.target.value)}
                    className="tw:w-16"
                  />
                </td>
                <td className={CELL_CLASS}>
                  <Select
                    value={phoneNumber.type}
                    onValueChange={next => editField(index, "type", next)}
                  >
                    <SelectTrigger className="tw:w-full">
                      <SelectValue>
                        {v => typeTextByValue.get(v) ?? v}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {PHONE_TYPE_OPTIONS.map(o => (
                        <SelectItem key={o.key} value={o.value}>
                          {o.text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className={CELL_CLASS}>
                  <Switch
                    checked={phoneNumber.is_primary === true}
                    onCheckedChange={checked =>
                      editField(index, "is_primary", checked)
                    }
                  />
                </td>
                {isUser ? (
                  <td className={`${CELL_CLASS} tw:text-center`}>
                    {phoneNumber.enabled ? (
                      <Check className="tw:size-4 tw:text-[#24B388]" />
                    ) : (
                      <ValidatePhoneNumberModal
                        phoneNumber={phoneNumber}
                        onClose={status =>
                          editField(index, "enabled", status === "success")
                        }
                      />
                    )}
                  </td>
                ) : (
                  <>
                    <td className={CELL_CLASS}>
                      <Switch
                        checked={phoneNumber.enabled === true}
                        onCheckedChange={checked =>
                          editField(index, "enabled", checked)
                        }
                      />
                    </td>
                    <td className={CELL_CLASS}>
                      <Switch
                        checked={phoneNumber.is_callable === true}
                        onCheckedChange={checked =>
                          editField(index, "is_callable", checked)
                        }
                      />
                    </td>
                  </>
                )}
                <td className={`${CELL_CLASS} tw:text-right`}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => removeNumber(index)}
                    aria-label="Remove phone number"
                  >
                    <X />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="tw:text-(--color-text-muted)">No phone numbers</p>
      )}
      <div>
        <Button type="button" variant="outline" size="sm" onClick={addNumber}>
          <Plus />
          Add Phone Number
        </Button>
      </div>
    </div>
  );
};

export default PhoneNumbersInput;
